import './styles.scss';

import React, {
    useEffect, useMemo, useRef, useState,
} from 'react';
import { connect } from 'react-redux';
import {
    Alert,
    Button,
    Checkbox,
    Empty,
    Input,
    Radio,
    Select,
    Spin,
    Tag,
} from 'antd';
import {
    CheckCircleFilled,
    ClockCircleOutlined,
    LeftOutlined,
    LockOutlined,
    RightOutlined,
    SearchOutlined,
} from '@ant-design/icons';
import Text from 'antd/lib/typography/Text';
import Title from 'antd/lib/typography/Title';

import { CombinedState } from 'reducers';
import { ObjectState } from 'cvat-core-wrapper';
import { LogType } from 'cvat-logger';
import getLabelDisplayName from 'utils/label-display';
import { frameToRelativeSeconds, getTimelineFrameRate } from 'utils/media-time';
import {
    fetchTranscriptAsync,
    updateAnnotationsAsync,
    updateJobAttrbutes,
} from 'actions/annotation-actions';
import {
    config_V2,
    getFilledFullText_V2,
    toDisplay_V2,
} from '../phase2-annotation-conf';
import { computeTranscriptCheckpointFrames } from '../standard-workspace/transcript-checkpoints';
import { selectedQuestionDependencyIsAvailable } from './manifest';

interface ManifestItem {
    id: string;
    name: string;
    source_attributes: string[];
    roles?: string[];
    enabled: boolean;
    note?: string;
}

interface ReviewManifest {
    version: string;
    title: string;
    instructions: string;
    source_phase: 'phase2a' | 'phase2b';
    checkpoint_mode: 'phase2a' | 'phase2b';
    routing_items?: ManifestItem[];
    items: ManifestItem[];
}

interface StateToProps {
    jobInstance: any;
    allStates: any[];
    jobAttributeSpec: any[];
    jobAttributes: Record<number, string>;
}

interface DispatchToProps {
    fetchTranscript(): void;
    onUpdateAnnotations(states: ObjectState[]): void;
    onUpdateJobAttributes(id: number, value: string): void;
}

function mapStateToProps(state: CombinedState): StateToProps {
    const {
        annotation: {
            job: {
                instance: jobInstance,
                attributes: { self: jobAttributeSpec },
            },
            annotations: {
                allStates,
                jobAttributes,
            },
        },
    } = state;

    return {
        jobInstance,
        allStates,
        jobAttributeSpec,
        jobAttributes,
    };
}

function mapDispatchToProps(dispatch: any): DispatchToProps {
    return {
        fetchTranscript(): void {
            dispatch(fetchTranscriptAsync());
        },
        onUpdateAnnotations(states: ObjectState[]): void {
            dispatch(updateAnnotationsAsync(states));
        },
        onUpdateJobAttributes(id: number, value: string): void {
            dispatch(updateJobAttrbutes(id, value));
        },
    };
}

function formatTimestamp(seconds: number): string {
    const safeSeconds = Math.max(0, Math.floor(seconds || 0));
    const minutes = Math.floor(safeSeconds / 60);
    const remainder = safeSeconds % 60;
    return `${minutes}:${remainder.toString().padStart(2, '0')}`;
}

function transcriptOptionLabel(option: string): string {
    return option.replace(/from video/gi, 'from transcript');
}

function personRole(labelName: string): 'police' | 'civilian' | 'other' {
    if (labelName.startsWith('person:Police')) {
        return 'police';
    }
    if (labelName.startsWith('person:Civilian')) {
        return 'civilian';
    }
    return 'other';
}

function personMatchesRubricRoles(labelName: string, roles: string[]): boolean {
    return roles.some((role) => {
        if (role === 'driver') {
            return labelName === 'person:Civilian:Driver';
        }
        if (role === 'civilian') {
            return labelName.startsWith('person:Civilian');
        }
        if (role === 'police') {
            return labelName.startsWith('person:Police');
        }
        return false;
    });
}

function parseRecurringValue(rawValue: string, frame: number): string {
    if (!rawValue) {
        return '';
    }

    try {
        const values = JSON.parse(rawValue);
        return values[frame] ?? '';
    } catch (_error) {
        return '';
    }
}

function setRecurringValue(rawValue: string, frame: number, value: string): string {
    let values: Record<number, string> = {};
    try {
        values = rawValue ? JSON.parse(rawValue) : {};
    } catch (_error) {
        values = {};
    }
    values[frame] = value;
    return JSON.stringify(values);
}

interface TextQuestionProps {
    value: string;
    onSave(value: string): void;
}

function TextQuestion(props: TextQuestionProps): JSX.Element {
    const { value, onSave } = props;
    const [draft, setDraft] = useState(value || '');

    useEffect(() => {
        setDraft(value || '');
    }, [value]);

    return (
        <Input.TextArea
            autoSize={{ minRows: 3, maxRows: 12 }}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onBlur={() => {
                if (draft !== value) {
                    onSave(draft);
                }
            }}
            placeholder='Enter your response'
        />
    );
}

interface QuestionInputProps {
    attribute: any;
    value: string;
    onChange(value: string): void;
}

function LikertQuestionInput(props: QuestionInputProps): JSX.Element {
    const {
        attribute,
        value,
        onChange,
    } = props;
    const [selectedValue, setSelectedValue] = useState(value || '');

    useEffect(() => {
        if (value) {
            setSelectedValue(value);
        }
    }, [value]);

    const minimum = Number.parseFloat(attribute.values[0]) || 1;
    const maximum = Number.parseFloat(attribute.values[1]) || 5;
    const step = Number.parseFloat(attribute.values[2]) || 1;
    const options = [];
    for (let option = minimum; option <= maximum; option += step) {
        options.push(option);
    }

    return (
        <div className='cvat-transcript-review-rating'>
            <Text type='secondary'>{config_V2[attribute.name]?.low || minimum}</Text>
            <div className='cvat-transcript-review-rating-control'>
                <Radio.Group
                    buttonStyle='solid'
                    value={selectedValue ? Number.parseFloat(selectedValue) : undefined}
                    onChange={(event) => {
                        const nextValue = `${event.target.value}`;
                        setSelectedValue(nextValue);
                        onChange(nextValue);
                    }}
                >
                    {options.map((option) => (
                        <Radio.Button key={option} value={option}>{option}</Radio.Button>
                    ))}
                </Radio.Group>
                {selectedValue && (
                    <Text className='cvat-transcript-review-rating-confirmation' aria-live='polite'>
                        <CheckCircleFilled />
                        {` Selected: ${selectedValue}`}
                    </Text>
                )}
            </div>
            <Text type='secondary'>{config_V2[attribute.name]?.high || maximum}</Text>
        </div>
    );
}

function QuestionInput(props: QuestionInputProps): JSX.Element {
    const {
        attribute,
        value,
        onChange,
    } = props;

    if (attribute.inputType === 'checkbox') {
        return (
            <Checkbox
                checked={value === 'true'}
                onChange={(event) => onChange(event.target.checked ? 'true' : 'false')}
            >
                Applies
            </Checkbox>
        );
    }

    if (attribute.inputType === 'number') {
        return <LikertQuestionInput {...props} />;
    }

    if (attribute.inputType === 'multiselect') {
        return (
            <Select
                mode='multiple'
                value={value ? value.split(',').filter(Boolean) : []}
                options={attribute.values.map((option: string) => ({
                    label: transcriptOptionLabel(option),
                    value: option,
                }))}
                onChange={(options: string[]) => onChange(options.join(','))}
                placeholder='Select all that apply'
            />
        );
    }

    if (attribute.inputType === 'text') {
        return <TextQuestion value={value} onSave={onChange} />;
    }

    return (
        <Select
            value={value || undefined}
            options={attribute.values.map((option: string) => ({
                label: transcriptOptionLabel(option),
                value: option,
            }))}
            onChange={onChange}
            placeholder='Select an answer'
        />
    );
}

function TranscriptReviewWorkspace(props: StateToProps & DispatchToProps): JSX.Element {
    const {
        jobInstance,
        allStates,
        jobAttributeSpec,
        jobAttributes,
        onUpdateAnnotations,
        onUpdateJobAttributes,
        fetchTranscript,
    } = props;
    const [manifest, setManifest] = useState<ReviewManifest | null>(null);
    const [transcriptData, setTranscriptData] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [activeStage, setActiveStage] = useState(0);
    const [revealStartSeconds, setRevealStartSeconds] = useState<number | null>(null);
    const [answerRevision, setAnswerRevision] = useState(0);
    const initializedStage = useRef(false);
    const transcriptSegmentsRef = useRef<HTMLDivElement>(null);

    const people = useMemo(
        () => allStates.filter((state) => state.label.name.startsWith('person:')),
        [allStates],
    );
    const frameRate = getTimelineFrameRate(jobInstance.dataFrameRate);

    useEffect(() => {
        let mounted = true;
        setLoading(true);
        fetchTranscript();
        Promise.all([
            jobInstance.transcript.get(),
            jobInstance.transcript.getReviewManifest(),
        ]).then(([transcript, reviewManifest]) => {
            if (!mounted) {
                return;
            }
            transcript.segments?.sort((left: any, right: any) => left.start - right.start);
            setTranscriptData(transcript);
            setManifest(reviewManifest);
            setLoading(false);
            if (jobInstance) {
                jobInstance.logger.log(LogType.loadJob, {
                    workspace: 'transcript_review',
                    phase: jobInstance.phase,
                }, false);
            }
        }).catch((error) => {
            if (mounted) {
                setLoadError(error?.message || 'The transcript review could not be loaded.');
                setLoading(false);
            }
        });

        return () => {
            mounted = false;
        };
    }, [jobInstance.id]);

    const checkpointFrames = useMemo(() => {
        if (!manifest || !transcriptData) {
            return [];
        }
        return computeTranscriptCheckpointFrames({
            mode: manifest.checkpoint_mode,
            transcriptData,
            people,
            startFrame: jobInstance.startFrame,
            stopFrame: jobInstance.stopFrame,
            frameRate,
        });
    }, [
        frameRate,
        jobInstance.startFrame,
        jobInstance.stopFrame,
        manifest,
        people,
        transcriptData,
    ]);

    const enabledItems = useMemo(
        () => manifest?.items.filter((item) => item.enabled) || [],
        [manifest],
    );
    const disabledItems = useMemo(
        () => manifest?.items.filter((item) => !item.enabled) || [],
        [manifest],
    );
    const questionItems = useMemo(
        () => [
            ...(manifest?.routing_items || []),
            ...enabledItems,
        ],
        [enabledItems, manifest],
    );
    const attributeItems = useMemo(() => {
        const mapping = new Map<string, ManifestItem>();
        questionItems.forEach((item) => {
            item.source_attributes.forEach((attribute) => {
                const existingItem = mapping.get(attribute);
                mapping.set(attribute, existingItem ? {
                    ...existingItem,
                    roles: Array.from(new Set([
                        ...(existingItem.roles || []),
                        ...(item.roles || []),
                    ])),
                } : item);
            });
        });
        return mapping;
    }, [questionItems]);
    const allowedAttributes = useMemo(
        () => new Set(attributeItems.keys()),
        [attributeItems],
    );

    const recurringStageCount = manifest?.source_phase === 'phase2b' ?
        Math.max(0, checkpointFrames.length - 1) :
        0;
    const stageCount = manifest?.source_phase === 'phase2a' ?
        checkpointFrames.length + 1 :
        recurringStageCount + 1;

    const stagePrefix = (stageIndex: number): string => {
        if (manifest?.source_phase === 'phase2a') {
            return stageIndex < checkpointFrames.length ?
                `phase2a.popup.${stageIndex}.` :
                'phase2a.sidebar.';
        }
        return stageIndex < recurringStageCount ?
            'phase2b.popup.recurring.' :
            'phase2b.popup.0.';
    };

    const stageFrame = (stageIndex: number): number => {
        if (manifest?.source_phase === 'phase2a') {
            return stageIndex < checkpointFrames.length ?
                checkpointFrames[stageIndex] :
                jobInstance.stopFrame - 1;
        }
        return stageIndex < recurringStageCount ?
            checkpointFrames[stageIndex] :
            jobInstance.stopFrame - 1;
    };

    const transcriptWindow = (stageIndex: number): [number, number] => {
        const endFrame = stageFrame(stageIndex);
        const previousFrame = stageIndex > 0 && stageIndex <= checkpointFrames.length ?
            checkpointFrames[Math.min(stageIndex - 1, checkpointFrames.length - 1)] :
            jobInstance.startFrame;
        return [
            frameToRelativeSeconds(previousFrame, jobInstance.startFrame, frameRate),
            frameToRelativeSeconds(endFrame, jobInstance.startFrame, frameRate),
        ];
    };

    const personHasTranscriptInStage = (person: any, stageIndex: number): boolean => {
        if (!transcriptData?.segments?.length) {
            return false;
        }

        const finalStage = stageIndex === stageCount - 1;
        const [windowStart, windowEnd] = transcriptWindow(stageIndex);
        return transcriptData.segments.some((segment: any) => {
            const [, , serverID] = (segment.speaker || '').split('_');
            const inWindow = finalStage ?
                segment.start <= windowEnd :
                segment.start >= windowStart && segment.start <= windowEnd;
            return `${person.serverID}` === serverID && inWindow;
        });
    };

    const stageAttributesForPerson = (person: any, stageIndex: number): any[] => {
        const prefix = stagePrefix(stageIndex);
        return person.label.attributes
            .filter((attribute: any) => (
                attribute.name.startsWith(prefix) &&
                allowedAttributes.has(attribute.name) &&
                personMatchesRubricRoles(
                    person.label.name,
                    attributeItems.get(attribute.name)?.roles || [],
                ) &&
                (
                    !selectedQuestionDependencyIsAvailable(
                        allowedAttributes,
                        config_V2[attribute.name]?.followupParent,
                    ) ||
                    toDisplay_V2(
                        person.label.attributes,
                        jobAttributeSpec,
                        person.attributes,
                        jobAttributes,
                        attribute,
                        people,
                    )
                )
            ))
            .sort((left: any, right: any) => (
                (config_V2[left.name] ? Object.keys(config_V2).indexOf(left.name) : Number.MAX_SAFE_INTEGER) -
                (config_V2[right.name] ? Object.keys(config_V2).indexOf(right.name) : Number.MAX_SAFE_INTEGER)
            ));
    };

    const readAttributeValue = (person: any, attribute: any, stageIndex: number): string => {
        const rawValue = person.attributes[attribute.id] || '';
        if (manifest?.source_phase === 'phase2b' && stageIndex < recurringStageCount) {
            return parseRecurringValue(rawValue, stageFrame(stageIndex));
        }
        return rawValue;
    };

    const updateAttributeValue = (
        person: any,
        attribute: any,
        stageIndex: number,
        value: string,
    ): void => {
        const nextValue = manifest?.source_phase === 'phase2b' && stageIndex < recurringStageCount ?
            setRecurringValue(person.attributes[attribute.id] || '', stageFrame(stageIndex), value) :
            value;
        person.attributes = {
            ...person.attributes,
            [attribute.id]: nextValue,
        };
        setAnswerRevision((revision) => revision + 1);
        onUpdateAnnotations([person]);
        if (jobInstance) {
            jobInstance.logger.log(LogType.changeAttribute, {
                attribute_id: attribute.id,
                attribute_name: attribute.name,
                stage: stageIndex,
                person_id: person.serverID,
            }, false);
        }
    };

    const stageIsComplete = (stageIndex: number): boolean => {
        if (!manifest || !stageCount) {
            return false;
        }

        const peopleComplete = people.every((person) => {
            const attributes = stageAttributesForPerson(person, stageIndex);
            if (!attributes.length || !personHasTranscriptInStage(person, stageIndex)) {
                return true;
            }
            return attributes.every((attribute) => (
                readAttributeValue(person, attribute, stageIndex) !== ''
            ));
        });

        const prefix = stagePrefix(stageIndex);
        const jobAttributesComplete = jobAttributeSpec
            .filter((attribute: any) => (
                attribute.name.startsWith(prefix) &&
                allowedAttributes.has(attribute.name)
            ))
            .every((attribute: any) => (jobAttributes[attribute.id] || '') !== '');

        return peopleComplete && jobAttributesComplete;
    };

    const completedStages = useMemo(
        () => Array.from({ length: stageCount }, (_value, index) => stageIsComplete(index)),
        [
            stageCount,
            allStates,
            jobAttributes,
            allowedAttributes,
            checkpointFrames,
            transcriptData,
            answerRevision,
        ],
    );
    const firstIncompleteStage = completedStages.findIndex((complete) => !complete);
    const maximumUnlockedStage = firstIncompleteStage === -1 ?
        Math.max(0, stageCount - 1) :
        firstIncompleteStage;

    useEffect(() => {
        if (stageCount && !initializedStage.current) {
            setActiveStage(maximumUnlockedStage);
            initializedStage.current = true;
        }
    }, [maximumUnlockedStage, stageCount]);

    const currentCutoffFrame = stageFrame(activeStage);
    const currentCutoffSeconds = frameToRelativeSeconds(
        currentCutoffFrame,
        jobInstance.startFrame,
        frameRate,
    );
    const [currentWindowStart] = transcriptWindow(activeStage);
    const isWholeStop = activeStage === stageCount - 1;

    const visibleSegments = useMemo(() => {
        const normalizedQuery = searchQuery.trim().toLowerCase();
        return (transcriptData?.segments || []).filter((segment: any) => {
            const inWindow = isWholeStop ?
                segment.start <= currentCutoffSeconds :
                segment.start >= currentWindowStart && segment.start <= currentCutoffSeconds;
            const matchesQuery = !normalizedQuery ||
                (segment.text || '').toLowerCase().includes(normalizedQuery);
            return inWindow && matchesQuery;
        });
    }, [currentCutoffSeconds, currentWindowStart, isWholeStop, searchQuery, transcriptData]);

    const newlyRevealedSegmentCount = revealStartSeconds === null ? 0 : visibleSegments.filter((segment: any) => (
        segment.start > revealStartSeconds && segment.start <= currentCutoffSeconds
    )).length;
    const firstNewlyRevealedSegmentIndex = revealStartSeconds === null ? -1 : visibleSegments.findIndex(
        (segment: any) => segment.start > revealStartSeconds && segment.start <= currentCutoffSeconds,
    );

    useEffect(() => {
        if (transcriptSegmentsRef.current) {
            transcriptSegmentsRef.current.scrollTop = 0;
        }
    }, [activeStage]);

    useEffect(() => {
        if (revealStartSeconds === null || !newlyRevealedSegmentCount) {
            return undefined;
        }

        const container = transcriptSegmentsRef.current;
        const firstNewSegment = container?.querySelector<HTMLElement>('[data-newly-revealed="true"]');
        if (container && firstNewSegment) {
            const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            const containerBounds = container.getBoundingClientRect();
            const segmentBounds = firstNewSegment.getBoundingClientRect();
            const targetScrollTop = Math.max(
                0,
                container.scrollTop + segmentBounds.top - containerBounds.top - container.clientHeight * 0.22,
            );
            container.scrollTo({
                top: targetScrollTop,
                behavior: reducedMotion ? 'auto' : 'smooth',
            });
        }

        const revealTimer = window.setTimeout(() => setRevealStartSeconds(null), 2600);
        return () => window.clearTimeout(revealTimer);
    }, [
        activeStage,
        currentCutoffSeconds,
        newlyRevealedSegmentCount,
        revealStartSeconds,
    ]);

    const stageLabel = (stageIndex: number): string => {
        if (manifest?.source_phase === 'phase2a') {
            return ['Early context', 'Middle context', 'End of stop', 'Whole-stop questions'][stageIndex] ||
                `Section ${stageIndex + 1}`;
        }
        return stageIndex < recurringStageCount ?
            `Context ${stageIndex + 1}` :
            'Whole-stop questions';
    };

    const selectStage = (nextStage: number): void => {
        setRevealStartSeconds(null);
        setActiveStage(nextStage);
        if (jobInstance) {
            jobInstance.logger.log(LogType.clickElement, {
                action: 'select_stage',
                stage_index: nextStage,
                stage_label: stageLabel(nextStage),
            }, false);
        }
    };

    const speakerName = (segment: any): string => {
        const [, , serverID] = (segment.speaker || '').split('_');
        const person = people.find((candidate) => `${candidate.serverID}` === serverID);
        if (!person) {
            return segment.speaker?.startsWith('RADIO') ? 'Radio' : 'Unknown speaker';
        }

        const identifier = person.label.attributes.find((attribute: any) => attribute.name === 'identifier');
        const suffix = identifier ? person.attributes[identifier.id] : '';
        return `${getLabelDisplayName(person.label.name)}${suffix ? ` ${suffix}` : ''}`;
    };

    const stageWindowText = (stageIndex: number): string => {
        if (stageIndex >= stageCount - 1) {
            return 'Full encounter';
        }
        const [startSec, endSec] = transcriptWindow(stageIndex);
        return `${formatTimestamp(startSec)} – ${formatTimestamp(endSec)}`;
    };

    const useDropdownNavigation = manifest?.source_phase === 'phase2b' || stageCount > 5;

    if (loading) {
        return (
            <div className='cvat-transcript-review-loading'>
                <Spin size='large' tip='Loading transcript review…' />
            </div>
        );
    }

    if (loadError || !manifest || !transcriptData) {
        return (
            <div className='cvat-transcript-review-loading'>
                <Alert
                    showIcon
                    type='error'
                    message='Transcript review unavailable'
                    description={loadError || 'The transcript or questionnaire manifest is missing.'}
                />
            </div>
        );
    }

    return (
        <main className='cvat-transcript-review-workspace'>
            <header className='cvat-transcript-review-header'>
                <div>
                    <div className='cvat-transcript-review-eyebrow'>Transcript-only annotation</div>
                    <Title level={3}>{manifest.title}</Title>
                    <Text type='secondary'>{manifest.instructions}</Text>
                </div>
                <div className='cvat-transcript-review-status'>
                    <Tag color='blue'>{manifest.version}</Tag>
                </div>
            </header>

            {disabledItems.length > 0 && (
                <Alert
                    className='cvat-transcript-review-manifest-alert'
                    type='warning'
                    showIcon
                    message={`${disabledItems.map((item) => item.name).join(', ')} not collected`}
                    description={disabledItems.map((item) => item.note).filter(Boolean).join(' ')}
                />
            )}

            {useDropdownNavigation ? (
                <nav
                    className='cvat-transcript-review-stages cvat-transcript-review-stages-dropdown'
                    aria-label='Transcript checkpoints'
                >
                    <div className='cvat-transcript-review-stages-nav-group'>
                        <Button
                            size='large'
                            type='default'
                            icon={<LeftOutlined />}
                            disabled={activeStage === 0}
                            onClick={() => selectStage(activeStage - 1)}
                        >
                            Previous
                        </Button>
                        <Select
                            size='large'
                            className='cvat-transcript-review-stage-select'
                            value={activeStage}
                            onChange={(nextStage) => selectStage(nextStage)}
                            dropdownMatchSelectWidth={false}
                            dropdownClassName='cvat-transcript-review-stage-dropdown-menu'
                        >
                            {Array.from({ length: stageCount }, (_value, index) => {
                                const unlocked = index <= maximumUnlockedStage;
                                const isComplete = completedStages[index];
                                let stageIcon = <LockOutlined style={{ color: '#bfbfbf' }} />;
                                if (isComplete) {
                                    stageIcon = <CheckCircleFilled style={{ color: '#52c41a' }} />;
                                } else if (unlocked) {
                                    stageIcon = <ClockCircleOutlined style={{ color: '#1890ff' }} />;
                                }
                                const label = stageLabel(index);
                                const windowText = stageWindowText(index);
                                return (
                                    <Select.Option
                                        key={index}
                                        value={index}
                                        disabled={!unlocked}
                                    >
                                        <span className='cvat-transcript-review-stage-option'>
                                            <span className='cvat-transcript-review-stage-option-title'>
                                                {stageIcon}
                                                <span
                                                    style={{
                                                        marginLeft: 8,
                                                        fontWeight: activeStage === index ? 600 : 400,
                                                    }}
                                                >
                                                    {label}
                                                </span>
                                                {windowText && (
                                                    <Text
                                                        type='secondary'
                                                        style={{ marginLeft: 6, fontSize: '12px' }}
                                                    >
                                                        {`(${windowText})`}
                                                    </Text>
                                                )}
                                            </span>
                                            {isComplete && (
                                                <Tag
                                                    color='success'
                                                    style={{
                                                        marginLeft: 12,
                                                        marginRight: 0,
                                                        fontSize: '11px',
                                                        lineHeight: '18px',
                                                    }}
                                                >
                                                    Done
                                                </Tag>
                                            )}
                                        </span>
                                    </Select.Option>
                                );
                            })}
                        </Select>
                        <Button
                            size='large'
                            type='default'
                            icon={<RightOutlined />}
                            disabled={
                                activeStage >= maximumUnlockedStage ||
                                activeStage >= stageCount - 1
                            }
                            onClick={() => selectStage(activeStage + 1)}
                        >
                            Next
                        </Button>
                    </div>
                    <div className='cvat-transcript-review-stages-counter'>
                        <Tag color={completedStages[activeStage] ? 'green' : 'blue'}>
                            {`${stageLabel(activeStage)} (${activeStage + 1} of ${stageCount})`}
                        </Tag>
                    </div>
                </nav>
            ) : (
                <nav className='cvat-transcript-review-stages' aria-label='Transcript checkpoints'>
                    {Array.from({ length: stageCount }, (_value, index) => {
                        const unlocked = index <= maximumUnlockedStage;
                        let stageIcon = <LockOutlined />;
                        if (completedStages[index]) {
                            stageIcon = <CheckCircleFilled />;
                        } else if (unlocked) {
                            stageIcon = <ClockCircleOutlined />;
                        }
                        return (
                            <Button
                                key={index}
                                type={activeStage === index ? 'primary' : 'default'}
                                disabled={!unlocked}
                                icon={stageIcon}
                                onClick={() => selectStage(index)}
                            >
                                {stageLabel(index)}
                            </Button>
                        );
                    })}
                </nav>
            )}

            <div className='cvat-transcript-review-grid'>
                <section className='cvat-transcript-review-transcript' aria-label='Transcript'>
                    <div className='cvat-transcript-review-pane-heading'>
                        <div>
                            <Title level={4}>Transcript</Title>
                            <Text type='secondary'>
                                {isWholeStop ?
                                    'Showing full encounter' :
                                    `Showing context from ${formatTimestamp(currentWindowStart)} ` +
                                    `through ${formatTimestamp(currentCutoffSeconds)}`}
                            </Text>
                        </div>
                        <Input
                            allowClear
                            value={searchQuery}
                            onChange={(event) => setSearchQuery(event.target.value)}
                            prefix={<SearchOutlined />}
                            placeholder='Search visible transcript'
                        />
                    </div>
                    <div className='cvat-transcript-review-segments' ref={transcriptSegmentsRef}>
                        {visibleSegments.length ? visibleSegments.map((segment: any, index: number) => {
                            const newlyRevealed = revealStartSeconds !== null &&
                                segment.start > revealStartSeconds &&
                                segment.start <= currentCutoffSeconds;
                            return (
                                <article
                                    className={`cvat-transcript-review-segment ${
                                        personRole(
                                            people.find((person) => (
                                                `${person.serverID}` === (segment.speaker || '').split('_')[2]
                                            ))?.label.name || '',
                                        )
                                    }${newlyRevealed ? ' cvat-transcript-review-segment-new' : ''}`}
                                    data-newly-revealed={newlyRevealed ? 'true' : undefined}
                                    key={segment.key ?? `${segment.start}-${segment.end}-${index}`}
                                    style={newlyRevealed ? {
                                        animationDelay: `${Math.min(index - firstNewlyRevealedSegmentIndex, 6) * 70}ms`,
                                    } : undefined}
                                >
                                    <div className='cvat-transcript-review-segment-meta'>
                                        <Text strong>
                                            {speakerName(segment)}
                                            {newlyRevealed && (
                                                <span className='cvat-transcript-review-new-badge'>New</span>
                                            )}
                                        </Text>
                                        <Text type='secondary'>
                                            {`${formatTimestamp(segment.start)}–${formatTimestamp(segment.end)}`}
                                        </Text>
                                    </div>
                                    <p>{segment.text}</p>
                                </article>
                            );
                        }) : (
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description={searchQuery ? 'No visible utterances match this search.' : 'No transcript text in this section.'}
                            />
                        )}
                    </div>
                </section>

                <aside className='cvat-transcript-review-questions' aria-label='Questions'>
                    <div className='cvat-transcript-review-pane-heading'>
                        <div>
                            <Title level={4}>{stageLabel(activeStage)}</Title>
                            <Text type='secondary'>
                                Answer from the visible transcript only.
                            </Text>
                        </div>
                        <Tag color={completedStages[activeStage] ? 'green' : 'gold'}>
                            {completedStages[activeStage] ? 'Complete' : 'In progress'}
                        </Tag>
                    </div>

                    <div className='cvat-transcript-review-question-list'>
                        {people.map((person) => {
                            const attributes = stageAttributesForPerson(person, activeStage);
                            if (!attributes.length) {
                                return null;
                            }
                            const appears = personHasTranscriptInStage(person, activeStage);
                            return (
                                <section className='cvat-transcript-review-person' key={person.clientID}>
                                    <div className='cvat-transcript-review-person-heading'>
                                        <Text strong>{getLabelDisplayName(person.label.name)}</Text>
                                        <Tag>{personRole(person.label.name)}</Tag>
                                    </div>
                                    {!appears ? (
                                        <Alert
                                            type='info'
                                            showIcon
                                            message='No utterance from this person is present in the visible transcript section.'
                                            description='Checkpoint questions for this person are skipped, matching the Phase 2 “Does Not Appear” control.'
                                        />
                                    ) : attributes.map((attribute) => {
                                        const value = readAttributeValue(person, attribute, activeStage);
                                        return (
                                            <div className='cvat-transcript-review-question' key={attribute.id}>
                                                <div className='cvat-transcript-review-question-title'>
                                                    <Text>
                                                        {getFilledFullText_V2(attribute.name, person.label.name)}
                                                    </Text>
                                                    {!value && <span className='cvat-transcript-review-required'>Required</span>}
                                                </div>
                                                <QuestionInput
                                                    attribute={attribute}
                                                    value={value}
                                                    onChange={(nextValue) => (
                                                        updateAttributeValue(person, attribute, activeStage, nextValue)
                                                    )}
                                                />
                                            </div>
                                        );
                                    })}
                                </section>
                            );
                        })}

                        {jobAttributeSpec
                            .filter((attribute: any) => (
                                attribute.name.startsWith(stagePrefix(activeStage)) &&
                                allowedAttributes.has(attribute.name)
                            ))
                            .map((attribute: any) => (
                                <div className='cvat-transcript-review-question' key={attribute.id}>
                                    <div className='cvat-transcript-review-question-title'>
                                        <Text>{config_V2[attribute.name]?.fullText || attribute.name}</Text>
                                    </div>
                                    <QuestionInput
                                        attribute={attribute}
                                        value={jobAttributes[attribute.id] || ''}
                                        onChange={(value) => {
                                            onUpdateJobAttributes(attribute.id, value);
                                            if (jobInstance) {
                                                jobInstance.logger.log(LogType.changeAttribute, {
                                                    attribute_id: attribute.id,
                                                    attribute_name: attribute.name,
                                                    stage: activeStage,
                                                }, false);
                                            }
                                        }}
                                    />
                                </div>
                            ))}
                    </div>

                    <footer className='cvat-transcript-review-question-footer'>
                        <Button
                            disabled={activeStage === 0}
                            onClick={() => setActiveStage((stage) => Math.max(0, stage - 1))}
                        >
                            Previous
                        </Button>
                        {activeStage < stageCount - 1 ? (
                            <Button
                                type='primary'
                                disabled={!completedStages[activeStage]}
                                onClick={() => selectStage(Math.min(stageCount - 1, activeStage + 1))}
                            >
                                Continue
                            </Button>
                        ) : (
                            <Text type={completedStages[activeStage] ? 'success' : 'secondary'}>
                                {completedStages[activeStage] ?
                                    'All questions are complete. Finish the job from the top menu.' :
                                    'Complete the remaining questions to finish this phase.'}
                            </Text>
                        )}
                    </footer>
                </aside>
            </div>
        </main>
    );
}

export default connect(mapStateToProps, mapDispatchToProps)(TranscriptReviewWorkspace);
